"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { ArrowRight, CarFront, X } from "lucide-react";
import ProductCard from "@/components/test/ProductCard";
import type { VeicoloDataVeicolo } from "@/interface/VeicoloDataVeicolo";

type UnavailableVehicle = {
    slug?: string;
    marca?: string;
    modello?: string;
    categoria?: string;
    tipoVeicolo?: string;
    articolo?: string;
    articolo2?: string;
    productId?: string;
};

type RecommendationVehicle = Partial<VeicoloDataVeicolo> & {
    slug?: string;
    prezzo?: string | number;
    km?: string | number;
    image?: string;
    foto?: string;
};

const RECOMMENDATION_LIMIT = 4;

function getRecommendationCriteria(vehicle: UnavailableVehicle): UnavailableVehicle {
    if (vehicle.marca || vehicle.modello || !vehicle.slug) {
        return vehicle;
    }

    const slugParts = vehicle.slug.split("-").filter(Boolean);
    return {
        ...vehicle,
        marca: slugParts[0],
        modello: slugParts[1],
    };
}

function normalizeVehicle(vehicle: RecommendationVehicle, index: number): VeicoloDataVeicolo {
    const marca = String(vehicle.marca || "Veicolo").trim();
    const modello = String(vehicle.modello || "Disponibile").trim();
    const articolo = String(vehicle.articolo || vehicle.productId || `recommended-${index}`).trim();

    return {
        slug: String(vehicle.slug || articolo).trim(),
        marca,
        modello,
        prezzo: String(vehicle.prezzo ?? "0"),
        colore: String(vehicle.colore || "-"),
        immatricolazione: vehicle.immatricolazione || "",
        alimentazione: String(vehicle.alimentazione || "-"),
        cambio: String(vehicle.cambio || "-"),
        km: vehicle.km ?? "-",
        categoria: String(vehicle.categoria || vehicle.tipoVeicolo || "-"),
        foto: String(vehicle.foto || vehicle.image || ""),
        foto_marca: String(vehicle.foto_marca || ""),
        articolo,
        rata: String(vehicle.rata || ""),
        image: String(vehicle.image || vehicle.foto || ""),
        productId: String(vehicle.productId || articolo),
        containerName: String(vehicle.containerName || ""),
        provincia: String(vehicle.provincia || "-"),
        localita: String(vehicle.localita || "-"),
        codiceIVA: String(vehicle.codiceIVA || ""),
        tipoVeicolo: String(vehicle.tipoVeicolo || vehicle.categoria || ""),
        flagArrivo: Number(vehicle.flagArrivo || 0),
    };
}

function extractVehicles(response: unknown) {
    const data = response as { data?: unknown; results?: unknown; vehicles?: unknown };
    const values = Array.isArray(response)
        ? response
        : Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data?.results)
                ? data.results
                : Array.isArray(data?.vehicles)
                    ? data.vehicles
                    : [];

    return values.map((vehicle, index) => normalizeVehicle(vehicle as RecommendationVehicle, index));
}

async function getRecommendations(vehicle: UnavailableVehicle) {
    const criteria = getRecommendationCriteria(vehicle);
    const baseUrl = String(process.env.NEXT_PUBLIC_API_URL || "").trim().replace(/\/$/, "");
    const response = await fetch(`${baseUrl}/api/articoli/scelta-migliore`, {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({
            filters: {
                ...(criteria.marca ? { marca: criteria.marca } : {}),
                ...(criteria.modello ? { modello: criteria.modello } : {}),
                ...(criteria.tipoVeicolo || criteria.categoria
                    ? { tipoVeicolo: criteria.tipoVeicolo || criteria.categoria }
                    : {}),
            },
            starredVehicles: [],
            limit: RECOMMENDATION_LIMIT,
            mode: "soft",
            excludeIds: [vehicle.articolo, vehicle.articolo2, vehicle.productId].filter(Boolean),
            listaVeicoliQuery: {
                page: 1,
                pageSize: 2000,
                typeSearch: "vendita",
                tipoUtenteListino: "PRI",
            },
        }),
    });

    if (!response.ok) {
        throw new Error(`Recommendations request failed: ${response.status}`);
    }

    return extractVehicles(await response.json());
}

export default function VehicleUnavailable({ vehicle = {} }: { vehicle?: UnavailableVehicle }) {
    const [recommendations, setRecommendations] = useState<VeicoloDataVeicolo[]>([]);

    useEffect(() => {
        let active = true;
        getRecommendations(vehicle)
            .then((vehicles) => {
                if (active) setRecommendations(vehicles);
            })
            .catch(() => {
                if (active) setRecommendations([]);
            });

        return () => {
            active = false;
        };
    }, [vehicle]);

    return (
        <main className="bg-white px-4 py-8 sm:px-6 lg:px-10">
            <section className="mx-auto max-w-7xl">
                <div className="flex min-h-[430px] flex-col items-center justify-center border-b border-slate-200 pb-12 text-center">
                    <div className="relative flex h-28 w-28 items-center justify-center rounded-full bg-slate-100">
                        <CarFront className="h-14 w-14 text-slate-800" strokeWidth={1.7} />
                        <span className="absolute bottom-1 right-0 flex h-11 w-11 items-center justify-center rounded-full bg-red-600 text-white">
                            <X className="h-7 w-7" strokeWidth={2.5} />
                        </span>
                    </div>
                    <h1 className="mt-8 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                        Questo veicolo è stato venduto o non è più disponibile
                    </h1>
                    <p className="mt-4 max-w-2xl text-base leading-7 text-slate-600 sm:text-lg">
                        Questo veicolo non è più disponibile. Possiamo però aiutarti a trovare un’auto simile tra quelle attualmente in vendita.
                    </p>
                    <div className="mt-8 flex w-full max-w-xl flex-col gap-3 sm:flex-row">
                        <a
                            href="#veicoli-consigliati"
                            className="inline-flex h-14 flex-1 items-center justify-center gap-2 rounded-md bg-gradient-custom px-6 text-base font-semibold text-white transition-opacity hover:opacity-90"
                        >
                            Vedi veicoli simili <ArrowRight className="h-5 w-5" />
                        </a>
                        <Link
                            href="/parco-veicoli"
                            className="inline-flex h-14 flex-1 items-center justify-center rounded-md border border-primary px-6 text-base font-semibold text-primary transition-colors hover:bg-primary/5"
                        >
                            Vai al parco auto
                        </Link>
                    </div>
                </div>

                <section id="veicoli-consigliati" className="py-10">
                    <div className="mb-6 flex items-end justify-between gap-4">
                        <h2 className="text-2xl font-bold text-slate-900 sm:text-3xl">Potrebbero interessarti anche</h2>
                        <Link href="/parco-veicoli" className="hidden shrink-0 items-center gap-2 text-sm font-semibold text-primary transition-colors hover:text-primary/80 sm:flex">
                            Vedi tutti i veicoli <ArrowRight className="h-5 w-5" />
                        </Link>
                    </div>

                    {recommendations.length > 0 ? (
                        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                            {recommendations.map((recommendation) => (
                                <ProductCard
                                    key={`${recommendation.slug}-${recommendation.productId}`}
                                    product={recommendation}
                                    compact
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="rounded-lg bg-slate-50 px-6 py-10 text-center text-slate-600">
                            Scopri tutti i veicoli attualmente disponibili nel nostro parco auto.
                        </div>
                    )}
                </section>
            </section>
        </main>
    );
}
